import {FormControl} from 'platform/components';
import {ActorRefFrom, SnapshotFrom} from 'xstate';

import {createContext} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {AuditCategoryOfStructure, AuditDataAssetsV2Body} from '@dms/api/core';
import {VehicleTypeEnumObject} from '@dms/api/shared';

import {VehicleCatalogueReturnType} from 'features/vehicle-catalogue';

import {machine} from '../core/machine/machine';
import {VehicleVariant} from '../core/machine/types/VehicleMachineContext';
import {BaseVehicle} from '../core/types/BaseVehicle';
import {FieldRegistry} from './FieldRegistry';

type MachineActor = ActorRefFrom<typeof machine>;

export interface VehicleFormContextValue<T extends BaseVehicle = BaseVehicle> {
  control: FormControl<T>;
  formApi: UseFormReturn<T>;

  /**
   * @deprecated Use `useWatch()` or `subscribe()` instead of `watch()` for better performance.
   * `watch()` causes unnecessary re-renders and can impact form performance.
   * @see https://react-hook-form.com/api/usewatch
   * @see https://react-hook-form.com/api/useform/subscribe
   */
  watch: UseFormReturn<T>['watch'];

  vehicleType: VehicleTypeEnumObject | null;

  catalogueApi?: VehicleCatalogueReturnType[0];

  globalState: SnapshotFrom<typeof machine>;
  sendMachineEvent: MachineActor['send'];

  /**
   * Vehicle variant information (sale, rental, service)
   * Accessible to all form components
   */
  vehicleVariant?: VehicleVariant;

  /**
   * Vehicle ID
   * Accessible to all form components
   */
  vehicleId: string | null;

  /**
   * Base test ID for all form components
   * Used to generate consistent test IDs like "{component-name}-{baseTestId}"
   * @default "vehicle-form"
   */
  baseTestId: string;

  /**
   * Array of selected mandatory fields that are marked as required (recommended FormField property)
   */
  mandatoryFields: string[];

  /**
   * Registry of form fields per wizard step for computing step indicators
   */
  fieldRegistry: FieldRegistry;

  auditContext: {
    setForm: (form: unknown) => void;
    getForm: () => unknown;
    getAssets: () => Record<string, AuditDataAssetsV2Body>;
    setAssets: (assets: Record<string, AuditDataAssetsV2Body>) => void;
    setStructure: (structure: AuditCategoryOfStructure[]) => void;
    getStructure: () => AuditCategoryOfStructure[];
  };

  /**
   * Unique session ID for this form instance
   * Used to distinguish between different form sessions to prevent stale data
   */
  sessionId: string | null;

  machineActor: ActorRefFrom<typeof machine>;
}

export const VehicleFormContext = createContext<VehicleFormContextValue<BaseVehicle> | null>(null);
